import AddressBlock from '@app/components/AddressBlock'
import AssociatedContactsSelector from '@app/components/AssociatedContactsSelector'
import CheckBox from '@app/components/CheckBox'
import CustomInput from '@app/components/CustomInput'
import DateSelector from '@app/components/DateSelector'
import { actionCreators as pipelineActionCreators } from '@app/components/PipelineEdit/modules/pipelineEdit'
import { FileDropzone, FileProgress } from '@app/components/FileUpload'
import FormGroupRenderer from '@app/components/FormGroupRenderer'
import ImageLinker from '@app/components/ImageLinker'
import LeaseTypeSelector from '@app/components/LeaseTypeSelector'
import ListingStatusSelector from '@app/components/ListingStatusSelector'
import ListingTypeSelector from '@app/components/ListingTypeSelector'
import PageSection from '@app/components/PageSection'
import PriceEditor from '@app/components/PriceEditor'
import RentalRateEditor from '@app/components/RentalRateEditor'
import RentDetailsEditor from '@app/components/RentDetailsEditor'
import StringListEditor from '@app/components/StringListEditor'
import { ListingType } from '@app/models/enums'
import { Listing } from '@app/models/Listing'
import { OnlineListingService } from '@app/models/OnlineListingService'
import { bindFunctions } from '@app/utils/class'
import { formatNumber } from '@app/utils/formatting'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as qs from 'query-string'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { RouteComponentProps } from 'react-router-dom'
import {
  Alert,
  Button,
  Col,
  Container,
  Input,
  InputGroup,
  InputGroupAddon,
  Row,
} from 'reactstrap'
import {
  actionCreators,
  IListingEditFiles,
  IListingEditState,
} from '../modules/listingEdit'
import ListingProgressV2StatusSelect from '@app/components/ListingProgressV2StatusSelect'
import { IGlobalConfigurationState } from '@app/store/modules/global'

interface IListingEditProps {
  quickAdd?: boolean
  propertyId?: string
  spaceId?: string
  lotId?: string
  listingId?: string
  className?: string
}

type ComponentEditProps = IListingEditState &
  IListingEditProps &
  IGlobalConfigurationState & {
    setCriticalDates: typeof pipelineActionCreators.setCriticalDates
  } & typeof actionCreators &
  RouteComponentProps<{ id?: string }> &
  IListingEditFiles

export default class ListingEdit extends React.PureComponent<
  ComponentEditProps
> {
  constructor(props: ComponentEditProps) {
    super(props)

    bindFunctions(this)
  }

  public componentDidMount() {
    if (this.props.listingId) {
      this.props.loadEditListing(this.props.listingId)
    } else {
      this.props.loadAddListing(
        this.props.propertyId,
        this.props.spaceId,
        this.props.lotId,
        this.props.quickAdd,
      )
    }
  }

  public componentWillUnmount() {
    this.props.resetState()
  }

  public render() {
    if (!this.props.isReady) {
      return (
        <Container className='my-3'>
          <div className='mb-3 text-center'>Retrieving Listing Information</div>
          {this.props.error && <Alert color='danger'>{this.props.error}</Alert>}
        </Container>
      )
    }

    const { quickAdd, listing } = this.props;
    return (
      <Container className={`my-3 ${this.props.className || ''}`}>
        {quickAdd
          ? (
            <>
              {this._renderGeneralInformation()}
              {listing.type === ListingType.Lease && this._renderLeaseInformation()}
              {(listing.type === ListingType.Sale || listing.type === ListingType.Development) && this._renderSaleInformation()}
            </>
          )
          : (
            <>
              {this._renderPropertyInformation()}
              {this._renderGeneralInformation()}
              {this._renderSaleInformation()}
              {this._renderRentDetails()}
              {this._renderLeaseInformation()}
              {this._renderHighlights()}
              {this._renderPropertyDescription()}
              {this._renderSignage()}
              {this._renderMarketing()}
              {this._renderListingServices()}
              {this._renderPhotos()}
              {this._renderVideos()}
              {this._renderTopProspects()}
              {this._renderAdjoiningPropertyOwners()}
              {this._renderTwoWeekDeadline()}
              {this._renderOngoing()}
            </>
          )
        }
      </Container>
    )
  }

  private _renderAdjoiningPropertyOwners() {
    return (
      <PageSection title='Adjoining Property Owners'>
        <AssociatedContactsSelector
          onChange={this.props.setAdjoiningPropertyOwners}
          placeholder='Add an adjoining property owner...'
          value={this.props.listing.adjoiningPropertyOwners}
        />
      </PageSection>
    )
  }

  private _renderTwoWeekDeadline() {
    return (
      <PageSection title='Two Week Deadline' collapse>
        <ListingProgressV2StatusSelect
          onChange={this.props.setGeneratedDocuments}
          value={
            this.props.listing.progressV2.twoWeekDeadline.generatedDocuments
          }
        >
          Generated applicable documents using form simplicity
        </ListingProgressV2StatusSelect>
        <ListingProgressV2StatusSelect
          onChange={this.props.setSentAndUploadedSignedListingAgreement}
          value={
            this.props.listing.progressV2.twoWeekDeadline
              .sentAndUploadedSignedListingAgreement
          }
        >
          Listing agreement signed, uploaded, executed copy sent to all parties
        </ListingProgressV2StatusSelect>
        <ListingProgressV2StatusSelect
          onChange={this.props.setSentOwnerThankyou}
          value={
            this.props.listing.progressV2.twoWeekDeadline.sentOwnerThankyou
          }
        >
          Send thank you note to owner(s)
        </ListingProgressV2StatusSelect>
        <ListingProgressV2StatusSelect
          onChange={this.props.setAttachedListingAgreement}
          value={
            this.props.listing.progressV2.twoWeekDeadline
              .attachedListingAgreement
          }
        >
          Listing agreement uploaded to software
        </ListingProgressV2StatusSelect>
        <ListingProgressV2StatusSelect
          onChange={this.props.setSentCriticalDatesBlast}
          value={
            this.props.listing.progressV2.twoWeekDeadline.sentCriticalDatesBlast
          }
        >
          Listing coordinator sends critical dates blast through software
        </ListingProgressV2StatusSelect>
        <ListingProgressV2StatusSelect
          onChange={this.props.setDeliveredSignage}
          value={this.props.listing.progressV2.twoWeekDeadline.deliveredSignage}
        >
          Signage delivered to listing site
        </ListingProgressV2StatusSelect>
        <ListingProgressV2StatusSelect
          onChange={this.props.setSentMarketingPackageOwner}
          value={
            this.props.listing.progressV2.twoWeekDeadline
              .sentMarketingPackageOwner
          }
        >
          Marketing package created - sent to owner for approval
        </ListingProgressV2StatusSelect>
        <ListingProgressV2StatusSelect
          onChange={this.props.setAttachedOwnerRevisions}
          value={
            this.props.listing.progressV2.twoWeekDeadline.attachedOwnerRevisions
          }
        >
          Any owner revisions made to marketing package
        </ListingProgressV2StatusSelect>
        <ListingProgressV2StatusSelect
          onChange={this.props.setUploadedPackageToPlatforms}
          value={
            this.props.listing.progressV2.twoWeekDeadline
              .uploadedPackageToPlatforms
          }
        >
          Upload owner approved package Loopnet, Crexi, CoStar
        </ListingProgressV2StatusSelect>
        <ListingProgressV2StatusSelect
          onChange={this.props.setSentOwnersListingPlatformLinks}
          value={
            this.props.listing.progressV2.twoWeekDeadline
              .sentOwnersListingPlatformLinks
          }
        >
          Send owner(s) links to listing platform services (e.g. Loopnet, Crexi,
          HB Website)
        </ListingProgressV2StatusSelect>
        <ListingProgressV2StatusSelect
          onChange={this.props.setSentMarketingPackageEDC}
          value={
            this.props.listing.progressV2.twoWeekDeadline
              .sentMarketingPackageEDC
          }
        >
          Send marketing package to Economic Development Commission
        </ListingProgressV2StatusSelect>
        <ListingProgressV2StatusSelect
          onChange={this.props.setSentPersonalEmailToProspects}
          value={
            this.props.listing.progressV2.twoWeekDeadline
              .sentPersonalEmailToProspects
          }
        >
          Send personal email to prospects
        </ListingProgressV2StatusSelect>
        <ListingProgressV2StatusSelect
          onChange={
            this.props.setAttachedTitleCommitmentPropertyDisclosureAndSurvey
          }
          value={
            this.props.listing.progressV2.twoWeekDeadline
              .attachedTitleCommitmentPropertyDisclosureAndSurvey
          }
        >
          Title commitment, property disclosure, survey if applicable
        </ListingProgressV2StatusSelect>
      </PageSection>
    )
  }

  private _renderOngoing() {
    return (
      <PageSection title='Ongoing' collapse>
        <ListingProgressV2StatusSelect
          onChange={this.props.setSentMonthlyFollowUps}
          value={this.props.listing.progressV2.ongoing.sentMonthlyFollowups}
        >
          Monthly followups with owner
        </ListingProgressV2StatusSelect>
        <ListingProgressV2StatusSelect
          onChange={this.props.setSentPersonalEmailToProspectsOngoing}
          value={
            this.props.listing.progressV2.ongoing.sentPersonalEmailToProspects
          }
        >
          Personal email to prospects
        </ListingProgressV2StatusSelect>
        <ListingProgressV2StatusSelect
          onChange={this.props.setSentMonthlyBlastListingPlatform}
          value={
            this.props.listing.progressV2.ongoing
              .sentMonthlyBlastListingPlatform
          }
        >
          Monthly blast on Loopnet and Crexi
        </ListingProgressV2StatusSelect>
      </PageSection>
    )
  }

  private _renderGeneralInformation() {
    return (
      <PageSection title='General Information' collapse>
        <FormGroupRenderer
          labelText='Title'
          type='text'
          name='Title'
          onChange={this.props.setTitle}
          required
          defaultValue={this.props.listing.title || this.props.listing.property.name}
          value={this.props.listing.title}
        />
        <FormGroupRenderer labelText='Listing Type'>
          <ListingTypeSelector
            name='ListingType'
            onChange={this.setListingType}
            required
            value={this.props.listing.type}
          />
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Status'>
          <ListingStatusSelector
            name='ListingStatus'
            onChange={this.props.setStatus}
            required
            value={this.props.listing.status}
          />
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Listing Agents'>
          <AssociatedContactsSelector
            name='ListingAgents'
            onChange={this.props.setListingAgents}
            placeholder='Add listing agent(s)...'
            required
            value={this.props.listing.listingAgents}
          />
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Listing Date'>
          <DateSelector
            value={this.props.listing.activeDate}
            onChange={this.props.setActiveDate}
          />
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Expiration Date'>
          <DateSelector
            value={this.props.listing.expirationDate}
            onChange={this.props.setExpirationDate}
          />
        </FormGroupRenderer>
        <FormGroupRenderer
          labelText='File Position No'
          onChange={this.props.setFilePositionNo}
          value={this.props.listing.filePositionNo}
        />
        <FormGroupRenderer>
          <CheckBox
            className='text-dark'
            onChange={this.props.setShowOnWebsite}
            value={this.props.listing.showOnWebsite}
          >
            Show on website
          </CheckBox>
          <CheckBox
            className='ml-3 text-dark'
            onChange={this.props.setIsFeatured}
            value={this.props.listing.isFeatured}
          >
            Featured listing
          </CheckBox>
          <CheckBox
            className='ml-3 text-dark'
            onChange={this.props.setReferral}
            value={this.props.listing.referral}
          >
            Referral
          </CheckBox>
        </FormGroupRenderer>
        {this.props.quickAdd && (
          <FormGroupRenderer>
            <CheckBox
              className='text-dark m-0'
              onChange={this.props.setNotHBListing}
              value={this.props.listing.notHBListing}
            >
              Not an HB listing
            </CheckBox>
            {this.props.listing.notHBListing && (
              <>
                <br />
                <small className='text-red ml-4'>Notifications and feed items will not be created for this listing.</small>
              </>
            )}
          </FormGroupRenderer>
        )}
      </PageSection>
    )
  }

  private _renderHighlights() {
    return (
      <PageSection title='Highlights'>
        <StringListEditor
          addLabel='Add highlight'
          name='Highlight'
          onChange={this.props.setHighlights}
          value={this.props.listing.highlights}
        />
      </PageSection>
    )
  }

  private _renderMarketing() {
    const visibleDropzoneStyle: React.CSSProperties = {
      border: '2px dashed #ddd',
      borderRadius: '0.5rem',
      height: 'auto',
      padding: '0.5rem 1rem',
      textAlign: 'center',
      width: '100%',
    }
    const hiddenDropzoneStyle: React.CSSProperties = {
      display: 'none',
    }

    return (
      <PageSection title='Marketing'>
        <FormGroupRenderer labelText='Flyer'>
          <FileDropzone
            identifier='listing-flyer'
            style={
              this.props.flyerFiles && this.props.flyerFiles.size > 0
                ? hiddenDropzoneStyle
                : visibleDropzoneStyle
            }
          >
            Drop file or click here to add a flyer
          </FileDropzone>
          <FileProgress
            identifier='listing-flyer'
            dataSource={this.props.listing.flyer}
          />
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Marketing Package'>
          <FileDropzone
            identifier='listing-marketing-pkg'
            style={
              this.props.marketingPackageFiles &&
              this.props.marketingPackageFiles.size > 0
                ? hiddenDropzoneStyle
                : visibleDropzoneStyle
            }
          >
            Drop file or click here to add a marketing package
          </FileDropzone>
          <FileProgress
            identifier='listing-marketing-pkg'
            dataSource={this.props.listing.marketingPackage}
          />
        </FormGroupRenderer>
      </PageSection>
    )
  }

  private _renderLeaseInformation() {
    if (
      this.props.listing.type !== ListingType.Lease ||
      !this.props.listing.leaseData
    ) {
      return null
    }

    const leaseData = this.props.listing.leaseData

    return (
      <PageSection title='Lease Information' collapse>
        <FormGroupRenderer labelText='Rental Rate'>
          <RentalRateEditor
            value={leaseData.rentalRate}
            name='RentalRate'
            required
            onChange={this.props.setRentalRate}
          />
        </FormGroupRenderer>
        <FormGroupRenderer
          labelText='Term (Months)'
          onChange={this.props.setLeaseTerm}
          type='number'
          value={leaseData.leaseTermInMonths}
        />
        <FormGroupRenderer labelText='Listing Fee'>
          <InputGroup>
            <CustomInput
              type='number'
              required
              name='Listing Fee'
              value={leaseData.commissionSplit.listingFee}
              step='0.01'
              onChange={(e) =>
                this.props.setListingFee(e, this.props.listing.type)
              }
            />
            <InputGroupAddon addonType='append'>%</InputGroupAddon>
          </InputGroup>
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Selling Fee'>
          <InputGroup>
            <CustomInput
              type='number'
              required
              name='Selling Fee'
              value={leaseData.commissionSplit.sellingFee}
              step='0.01'
              onChange={(e) =>
                this.props.setSellingFee(e, this.props.listing.type)
              }
            />
            <InputGroupAddon addonType='append'>%</InputGroupAddon>
          </InputGroup>
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Lease Type'>
          <LeaseTypeSelector
            required
            name='LeaseType'
            value={leaseData.leaseType}
            onChange={this.props.setLeaseType}
          />
        </FormGroupRenderer>
        <FormGroupRenderer>
          <CheckBox
            onChange={this.props.setIsSublease}
            value={leaseData.sublease}
          >
            Is this a sublease?
          </CheckBox>
        </FormGroupRenderer>
        <FormGroupRenderer>
          <CheckBox
            onChange={this.props.setIsBuildToSuit}
            value={leaseData.buildToSuit}
          >
            Does this lease offer a build to suit option?
          </CheckBox>
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Date Available'>
          <DateSelector
            value={leaseData.dateAvailable}
            onChange={this.props.setDateAvailable}
          />
        </FormGroupRenderer>
      </PageSection>
    )
  }

  private _renderListingServices() {
    return (
      <PageSection title='Listing Services'>
        <div className='mb-2'>
          <Button
            color='primary'
            onClick={this.props.addOnlineListingService}
            outline
            size='sm'
          >
            Add another listing service
          </Button>
        </div>
        {this.props.listing.listingServices.toArray().map((ls, i) => {
          const labelContent =
            ls.required === true ? (
              ls.name
            ) : (
              <Input
                type='text'
                name={`ListingService${i + 1}Name`}
                value={ls.name}
                onChange={(e) => {
                  this.props.setOnlineListingService(
                    i,
                    new OnlineListingService({
                      link: ls.link,
                      name: e.target.value,
                      required: ls.required,
                    }),
                  )
                }}
                placeholder='Service name'
                required
              />
            )

          return (
            <FormGroupRenderer key={`listing-svc-${i}`} label={labelContent}>
              <InputGroup>
                <Input
                  type='text'
                  value={ls.link}
                  name='ListingServiceURL'
                  onChange={(e) => {
                    this.props.setOnlineListingService(
                      i,
                      new OnlineListingService({
                        link: e.target.value,
                        name: ls.name,
                        required: ls.required,
                      }),
                    )
                  }}
                  placeholder='Link to the listing'
                />
                {ls.required !== true && (
                  <InputGroupAddon addonType='append'>
                    <Button
                      color='danger'
                      onClick={() => this.props.deleteOnlineListingService(i)}
                      outline
                    >
                      <FontAwesomeIcon icon={['fal', 'trash']} fixedWidth />
                    </Button>
                  </InputGroupAddon>
                )}
              </InputGroup>
            </FormGroupRenderer>
          )
        })}
      </PageSection>
    )
  }

  private _renderPhotos() {
    return (
      <PageSection title='Photos' collapse>
        <Row>
          {this.props.listing.property.photos.toArray().map((p) => (
            <Col key={p.id} md={6} xl={4}>
              <ImageLinker
                className='mt-3'
                image={p}
                isLinked={
                  this.props.listing.photos.findIndex(
                    (lp) => lp !== undefined && lp.id === p.id,
                  ) > -1
                }
                isMain={this.props.listing.thumbnailImageId === p.id}
                onLink={this.props.addLinkedImage}
                onSetAsMain={this.props.setMainImage}
                onUnlink={this.props.deleteLinkedImage}
                parentType='listing'
              />
            </Col>
          ))}
        </Row>
      </PageSection>
    )
  }

  private _renderPropertyDescription() {
    return (
      <PageSection title='Property Description'>
        <Input
          type='textarea'
          onChange={(e) => this.props.setPropertyDescription(e.target.value)}
          style={{ minHeight: '8rem', resize: 'vertical' }}
          value={this.props.listing.propertyDescription}
        />
      </PageSection>
    )
  }

  private _renderPropertyInformation() {
    return (
      <PageSection title='Property Information' collapse>
        <FormGroupRenderer labelText='Name'>
          {this.props.listing.property.name}
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Address'>
          <AddressBlock address={this.props.listing.property.address} />
        </FormGroupRenderer>
        {this.props.listing.property.totalBuildingSize > 0 && (
          <FormGroupRenderer labelText='Total Building Size'>
            {formatNumber(this.props.listing.property.totalBuildingSize)} SF
          </FormGroupRenderer>
        )}
        {this.props.listing.property.totalLotSize.lotSize > 0 && (
          <FormGroupRenderer labelText='Total Lot Size'>
            {this.props.listing.property.totalLotSize.toString()}
          </FormGroupRenderer>
        )}
        {this.props.listing.property.lotDetails.size > 0 && (
          <FormGroupRenderer labelText='Listing for Lot'>
            <Input
              onChange={(e) => this.props.setLot(e.target.value)}
              type='select'
              value={this.props.listing.lotId}
            >
              <option value=''>None</option>
              {this.props.listing.property.lotDetails.toArray().map((l) => (
                <option key={l.id} value={l.id}>
                  {l.lotNo}
                </option>
              ))}
            </Input>
          </FormGroupRenderer>
        )}
        {this.props.listing.lot && (
          <FormGroupRenderer labelText='Lot Size'>
            {this.props.listing.lot.lotSize.toString()}
          </FormGroupRenderer>
        )}
        {this.props.listing.property.spaceDetails.size > 0 && (
          <FormGroupRenderer labelText='Listing for Space'>
            <Input
              onChange={(e) => this.props.setSpace(e.target.value)}
              type='select'
              value={this.props.listing.spaceId}
            >
              <option value=''>None</option>
              {this.props.listing.property.spaceDetails.toArray().map((s) => (
                <option key={s.id} value={s.id}>
                  {s.spaceNo}
                </option>
              ))}
            </Input>
          </FormGroupRenderer>
        )}
        {this.props.listing.space && (
          <FormGroupRenderer labelText='Space Available'>
            {this.props.listing.space.spaceAvailable.toString()}
          </FormGroupRenderer>
        )}
      </PageSection>
    )
  }

  private _renderRentDetails() {
    if (
      this.props.listing.type !== ListingType.Sale &&
      !this.props.listing.isInvestmentProperty
    ) {
      return null
    }

    const rentDetails = this.props.listing.financialData.rentDetails

    return (
      <PageSection title='Rent Details'>
        <RentDetailsEditor
          value={rentDetails}
          onChange={this.props.setRentDetails}
        />
      </PageSection>
    )
  }

  private _renderSaleInformation() {
    if (
      (this.props.listing.type !== ListingType.Sale &&
        this.props.listing.type !== ListingType.Development) ||
      !this.props.listing.saleData
    ) {
      return null
    }

    const saleData = this.props.listing.saleData

    return (
      <PageSection title='Sale Information'>
        <FormGroupRenderer labelText='Asking Price'>
          <PriceEditor
            required
            name='AskingPrice'
            value={saleData.askingPrice}
            onChange={this.props.setAskingPrice}
          />
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Listing Fee'>
          <InputGroup>
            <CustomInput
              type='number'
              required
              name='ListingFee'
              value={saleData.commissionSplit.listingFee}
              step='0.01'
              onChange={(e) =>
                this.props.setListingFee(e, this.props.listing.type)
              }
            />
            <InputGroupAddon addonType='append'>%</InputGroupAddon>
          </InputGroup>
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Selling Fee'>
          <InputGroup>
            <CustomInput
              type='number'
              required
              name='SellingFee'
              value={saleData.commissionSplit.sellingFee}
              step='0.01'
              onChange={(e) =>
                this.props.setSellingFee(e, this.props.listing.type)
              }
            />
            <InputGroupAddon addonType='append'>%</InputGroupAddon>
          </InputGroup>
        </FormGroupRenderer>
        <FormGroupRenderer
          labelText='Cap Rate'
          type='number'
          name='CapRate'
          value={saleData.capRate}
          step='0.01'
          onChange={this.props.setCapRate}
        />
        {this.props.listing.type === ListingType.Sale && (
          <FormGroupRenderer>
            <CheckBox
              onChange={this.props.setIsInvestmentProperty}
              value={this.props.listing.isInvestmentProperty}
            >
              Is an investment property?
            </CheckBox>
          </FormGroupRenderer>
        )}
        {this.props.listing.type === ListingType.Sale &&
          this.props.listing.isInvestmentProperty && (
            <div>
              <FormGroupRenderer
                labelText='Gross Income'
                type='number'
                name='GrossIncome'
                value={this.props.listing.financialData.grossIncome}
                step='0.01'
                onChange={this.props.setGrossIncome}
              />
              <FormGroupRenderer
                labelText='Net Income'
                type='number'
                name='NetIncome'
                value={this.props.listing.financialData.netIncome}
                step='0.01'
                onChange={this.props.setNetIncome}
              />
            </div>
          )}
      </PageSection>
    )
  }

  private _renderSignage() {
    return (
      <PageSection title='Signage'>
        <FormGroupRenderer>
          <ListingProgressV2StatusSelect
            onChange={this.props.setDeliveredSignage}
            value={
              this.props.listing.progressV2.twoWeekDeadline.deliveredSignage
            }
          >
            Has the Signage been delivered
          </ListingProgressV2StatusSelect>
        </FormGroupRenderer>
        <FormGroupRenderer labelText='Notes'>
          <Input
            type='textarea'
            onChange={(e) => this.props.setSignageNotes(e.target.value)}
            style={{ minHeight: '4rem', resize: 'vertical' }}
            value={this.props.listing.signageNotes}
          />
        </FormGroupRenderer>
      </PageSection>
    )
  }

  private _renderTopProspects() {
    return (
      <PageSection title='Top Prospects'>
        <AssociatedContactsSelector
          onChange={this.props.setTopProspects}
          placeholder='Add a prospect...'
          value={this.props.listing.topProspects}
        />
      </PageSection>
    )
  }

  private _renderVideos() {
    return (
      <PageSection title='Videos'>
        <StringListEditor
          addLabel='Add video link'
          onChange={this.props.setVideoLinks}
          type='url'
          value={this.props.listing.videoLinks}
        />
      </PageSection>
    )
  }

  private setListingType = (listingType: string) => {
    if (this.props.quickAdd) {
      if (listingType === ListingType.Lease) {
        this.props.setCriticalDates(
          this.props.configuration.brokeragePipelineCriticalDates
            .find((x) => x?.get('type') === ListingType.Lease)
            ?.get('criticalDates'),
        )
      } else {
        this.props.setCriticalDates(
          this.props.configuration.brokeragePipelineCriticalDates
            .find((x) => x?.type !== ListingType.Lease)
            ?.get('criticalDates') ?? [],
        )
      }
    }

    this.props.setType(listingType)
  }
}
